import { ReactNode, useEffect, useState, createContext, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';
import { generateId } from '../utils/id';

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  id: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastProps {
  toast: ToastData;
  onClose: (id: string) => void;
}

/**
 * Toast - Individual toast notification component
 * 
 * Required CSS classes:
 * - .toast - Base toast container
 * - .toast-success, .toast-error, .toast-warning, .toast-info - Variant styles
 * - .toast-message - Message text
 * - .toast-action - Action button
 * - .toast-close - Close button
 * 
 * Accessibility:
 * - Keyboard: Escape (close), Enter/Space on action button
 * - Screen reader: Announces toast message and variant
 * - Focus: Focuses action button if present
 * 
 * @example
 * <Toast toast={toast} onClose={handleClose} />
 */
export function Toast({ toast, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onClose(toast.id), 300); // Wait for exit animation
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  return (
    <div
      className={cn(
        'toast',
        `toast-${toast.variant || 'info'}`,
        isExiting && 'toast-exiting'
      )}
      role="alert"
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
    >
      <span className="toast-message">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => {
            toast.action?.onClick();
            handleClose();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-close"
        aria-label="Close notification"
        onClick={handleClose}
      >
        ×
      </button>
    </div>
  );
}

interface ToastContextValue {
  showToast: (message: string, options?: Partial<ToastData>) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

/**
 * ToastProvider - Context provider for toast notifications
 * 
 * Required CSS classes:
 * - .toast-container - Container for all toasts
 * - .toast-container-top-left, etc. - Position variants
 * 
 * @example
 * <ToastProvider position="top-right" maxToasts={5}>
 *   <App />
 * </ToastProvider>
 */
export function ToastProvider({ children, position = 'top-right', maxToasts = 5 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((message: string, options?: Partial<ToastData>): string => {
    const id = options?.id || generateId('toast');
    const newToast: ToastData = {
      id,
      message,
      variant: options?.variant || 'info',
      duration: options?.duration ?? 5000,
      action: options?.action,
    };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      return updated.slice(-maxToasts); // Keep only last N toasts
    });

    return id;
  }, [maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      {toasts.length > 0 &&
        createPortal(
          <div className={cn('toast-container', `toast-container-${position}`)} role="region" aria-label="Notifications">
            {toasts.map((toast) => (
              <Toast key={toast.id} toast={toast} onClose={removeToast} />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

/**
 * useToast - Hook to show toast notifications
 * 
 * @example
 * const { showToast } = useToast();
 * showToast('Success!', { variant: 'success' });
 * showToast('Error occurred', { variant: 'error', duration: 0 }); // No auto-dismiss
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// Convenience functions
export function useToastHelpers() {
  const { showToast } = useToast();

  return {
    success: (message: string, options?: Partial<ToastData>) =>
      showToast(message, { ...options, variant: 'success' }),
    error: (message: string, options?: Partial<ToastData>) =>
      showToast(message, { ...options, variant: 'error' }),
    warning: (message: string, options?: Partial<ToastData>) =>
      showToast(message, { ...options, variant: 'warning' }),
    info: (message: string, options?: Partial<ToastData>) =>
      showToast(message, { ...options, variant: 'info' }),
  };
}
